/**
 * EffectChip.js — Chip for a single effect in the THEN pipeline
 *
 * Shows a summary label in collapsed state.
 * Click to expand for inline editing via ChipExpansionPanel.
 */

import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';

const getEffectLabel = (effect) => {
  if (!effect) return 'effect';
  switch (effect.type) {
    case 'set_session_state':
      return effect.stateKey ? `Set State: ${effect.stateKey}` : 'Set State';
    case 'clear_session_state':
      return effect.stateKey ? `Clear State: ${effect.stateKey}` : 'Clear State';
    case 'set_character_flag':
      return effect.flag ? `Set Flag: ${effect.flag}` : 'Set Flag';
    case 'clear_character_flag':
      return effect.flag ? `Clear Flag: ${effect.flag}` : 'Clear Flag';
    case 'toggle_character_flag':
      return effect.flag ? `Toggle Flag: ${effect.flag}` : 'Toggle Flag';
    case 'gain_hope':
      return `+${effect.amount || 1} Hope`;
    case 'clear_stress':
      return `Clear ${effect.amount || 1} Stress`;
    case 'mark_stress':
      return `Mark ${effect.amount || 1} Stress`;
    case 'heal_hp':
      return `Heal ${effect.amount || 1} HP`;
    case 'gain_token':
      return effect.tokenId ? `+${effect.amount || 1} Token: ${effect.tokenId}` : 'Gain Token';
    case 'reset_token':
      return effect.tokenId ? `Reset Token: ${effect.tokenId}` : 'Reset Token';
    case 'initialize_token':
      return effect.tokenId ? `Init Token: ${effect.tokenId}` : 'Init Token';
    case 'grant_armor':
      return `+${effect.amount || 1} Armor`;
    case 'set_advantage':
      return 'Set Advantage';
    case 'add_bonus_dice':
      return `+${effect.amount || 1} Bonus Dice`;
    case 'trigger_spell_attack':
      return 'Spell Attack';
    case 'trigger_trait_roll':
      return 'Trait Roll';
    case 'trigger_weapon_roll':
      return 'Weapon Roll';
    case 'heal_ally':
      return `Heal Ally ${effect.amount || 1}`;
    case 'clear_ally_stress':
      return `Clear Ally Stress`;
    case 'grant_ally_hope':
      return `Grant Ally Hope`;
    case 'grant_ally_armor':
      return `Grant Ally Armor`;
    case 'show_toast':
      return effect.message ? `Message: ${effect.message}` : 'Show Message';
    case 'switch_tab':
      return effect.tab ? `Switch Tab: ${effect.tab}` : 'Switch Tab';
    case 'roll_local_dice':
      return 'Roll Dice';
    default:
      return effect.type || 'effect';
  }
};

const EffectChip = ({ item, index, isExpanded, onToggleExpand, onRemove, colorScheme }) => (
  <div
    className={cn(
      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm cursor-pointer',
      'transition-all',
      colorScheme.chipBg,
      colorScheme.chipText,
      isExpanded && 'ring-1 ring-green-500'
    )}
  >
    <button
      type="button"
      onClick={() => onToggleExpand(index)}
      aria-expanded={isExpanded}
      aria-label={`Edit effect: ${getEffectLabel(item)}`}
      className="select-none text-sm bg-transparent border-0 p-0 cursor-pointer text-inherit"
    >
      {getEffectLabel(item)}
    </button>
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRemove(index); }}
      aria-label={`Remove effect: ${getEffectLabel(item)}`}
      className="ml-0.5 hover:text-white transition-colors p-1 min-w-[28px] min-h-[28px] flex items-center justify-center"
    >
      <X size={14} />
    </button>
  </div>
);

export { getEffectLabel };
export default EffectChip;
